/**
 * DuplicateWorkOrders — admin cleanup tool for accidentally-duplicated
 * WOs. Groups every non-deleted WO by (lowered title, siteId, created-
 * on day) and shows each cluster with a bulk-purge action.
 *
 * Motivation: on production, admins found the same "Replace main
 * motor" WO created 5x on the same day — impossible to nuke one-by-
 * one when the admin WO list truncated to the newest few. This tool
 * surfaces every cluster in one page and lets the admin wipe them
 * with a single confirm.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import api from '../../services/api';

const listDuplicates = () =>
  api.get('/admin/work-orders/duplicates').then((r) => r.data);

const purgeCluster = (woIds, forceDetach = true) =>
  api.post('/admin/work-orders/purge-cluster', { woIds, forceDetach }).then((r) => r.data);

const DuplicateWorkOrders = () => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // Track which cluster row is expanded to show per-WO checkboxes.
  const [expanded, setExpanded] = useState({});
  const [selectedIds, setSelectedIds] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await listDuplicates();
      setClusters(r.clusters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalDupes = useMemo(
    () => clusters.reduce((s, c) => s + (c.count - 1), 0),
    [clusters],
  );

  const runPurge = async (cluster, ids) => {
    if (!ids.length) {
      toast.error('Nothing selected');
      return;
    }
    const clusterKey = clusterKeyOf(cluster);
    if (!window.confirm(`Permanently delete ${ids.length} work order${ids.length === 1 ? '' : 's'} from "${cluster.title}" on ${cluster.day}?\n\nThis cannot be undone.`)) return;
    setBusy(clusterKey);
    try {
      const res = await purgeCluster(ids, true);
      toast.success(`Purged ${res.purged}${res.blocked ? ` · ${res.blocked} blocked (check invoice status)` : ''}`);
      if (res.blocked) {
        const blockedList = res.results.filter((r) => !r.ok);
        console.warn('Blocked purges:', blockedList);
      }
      await load();
      setSelectedIds((s) => ({ ...s, [clusterKey]: [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk purge failed');
    } finally {
      setBusy(null);
    }
  };

  const clusterKeyOf = (c) => `${c.title}|${c.siteId}|${c.day}`;

  const toggleId = (clusterKey, id) => {
    setSelectedIds((s) => {
      const cur = s[clusterKey] || [];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...s, [clusterKey]: next };
    });
  };

  const selectAllButNewest = (cluster) => {
    // Keep the newest WO (latest createdAt) and mark all others for
    // purge — the most common "correct" cleanup.
    const clusterKey = clusterKeyOf(cluster);
    const sorted = [...cluster.wos].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    const toPurge = sorted.slice(1).map((w) => w.id);
    setSelectedIds((s) => ({ ...s, [clusterKey]: toPurge }));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="duplicate-work-orders-page">
      <PageHeader
        title="Duplicate Work Orders"
        description="Every group of 2+ WOs sharing the same title, site and creation day. Use this to hard-delete accidental duplicates in one shot."
        actions={
          <Button variant="outline" onClick={load} data-testid="dupes-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Duplicate clusters</div>
          <div className="text-3xl font-display text-slate-900">{clusters.length}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500">Extra WOs cluttering the DB</div>
          <div className="text-3xl font-display text-red-700">{totalDupes}</div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 text-sm">
          Scanning for duplicates…
        </div>
      ) : clusters.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-emerald-700 text-sm">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
          Nice — no duplicate work orders detected.
        </div>
      ) : (
        <div className="space-y-3">
          {clusters.map((cluster) => {
            const clusterKey = clusterKeyOf(cluster);
            const isOpen = !!expanded[clusterKey];
            const selected = selectedIds[clusterKey] || [];
            const isBusy = busy === clusterKey;
            return (
              <div
                key={clusterKey}
                className="bg-white border border-slate-200 rounded-md overflow-hidden"
                data-testid={`dupe-cluster-${clusterKey}`}
              >
                <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-100">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {cluster.title || <em className="text-slate-400">(untitled)</em>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {cluster.customerName || '—'} · {cluster.siteName || 'no site'} · {cluster.day || 'no date'}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    {cluster.count}× duplicated
                  </span>
                  <button
                    type="button"
                    className="text-xs text-blue-700 hover:text-blue-800 font-medium"
                    onClick={() => setExpanded((s) => ({ ...s, [clusterKey]: !isOpen }))}
                    data-testid={`dupe-expand-${clusterKey}`}
                  >
                    {isOpen ? 'Hide' : 'Show'} WOs
                  </button>
                </div>
                {isOpen && (
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => selectAllButNewest(cluster)}
                        className="text-blue-700 hover:text-blue-800 font-medium"
                        data-testid={`dupe-select-all-but-newest-${clusterKey}`}
                      >
                        Select all but newest ({cluster.count - 1})
                      </button>
                      <span className="text-slate-500">
                        {selected.length} selected of {cluster.count}
                      </span>
                    </div>
                    <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
                      {[...cluster.wos]
                        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
                        .map((w, idx) => (
                          <li key={w.id} className="flex items-center gap-3 p-2.5 text-sm">
                            <input
                              type="checkbox"
                              checked={selected.includes(w.id)}
                              onChange={() => toggleId(clusterKey, w.id)}
                              data-testid={`dupe-wo-checkbox-${w.id}`}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                            <span className="font-mono text-xs text-slate-500 w-28 shrink-0">{w.id}</span>
                            <span className="flex-1 min-w-0 truncate text-slate-800">{w.status}</span>
                            <span className="text-xs text-slate-500 w-40 truncate">{w.invoiceId ? `→ ${w.invoiceId}` : '—'}</span>
                            {idx === 0 && (
                              <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                                newest
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                    <div className="flex justify-end">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => runPurge(cluster, selected)}
                        disabled={isBusy || !selected.length}
                        data-testid={`dupe-purge-cluster-${clusterKey}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {isBusy ? 'Purging…' : `Purge ${selected.length} selected`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DuplicateWorkOrders;
